/**
 * The stale-response guard behind one level of the location cascade
 * (province / district / neighbourhood), extracted from the React hook that
 * uses it so it can be tested without a component test runner.
 *
 * A level is reloaded every time its parent changes (a new province picked
 * reloads the district list) and can also be reloaded by hand (a failed
 * fetch's retry button). Either can leave more than one request in flight at
 * once, and network order is not request order — an old request can resolve
 * or reject after a newer one already has. Applying a stale response would
 * silently show the wrong list with real, selectable ids and no error, which
 * is worse than showing nothing.
 *
 * The guard is a generation counter: starting a load stamps it with the
 * state's new generation, and a response only takes effect if its generation
 * still matches the state's generation when it arrives. A later load bumps
 * the generation first, so anything still in flight for the old one is
 * automatically stale and gets dropped — success or failure alike.
 */

export interface LevelLoadState<T> {
  items: T[];
  failed: boolean;
  generation: number;
}

export function initialLevelLoadState<T>(): LevelLoadState<T> {
  return { items: [], failed: false, generation: 0 };
}

/**
 * Begins a new load attempt. Always bumps the generation, so any response
 * still in flight for a previous attempt is stale from this point on. When
 * there is no key to load for (the level's parent was cleared), the items and
 * error are cleared immediately rather than waiting on a fetch that will
 * never be started.
 *
 * Returns the new state to store and the generation to tag the eventual
 * response with — pass that generation back into `applyLevelSuccess` /
 * `applyLevelFailure` when the fetch settles.
 */
export function startLevelLoad<T>(
  state: LevelLoadState<T>,
  hasKey: boolean
): { state: LevelLoadState<T>; generation: number } {
  const generation = state.generation + 1;
  if (!hasKey) {
    return { state: { items: [], failed: false, generation }, generation };
  }
  return { state: { ...state, failed: false, generation }, generation };
}

/**
 * Applies a successful fetch's result, unless a newer load has started since
 * this one began — in which case the response is dropped and the state is
 * returned unchanged.
 */
export function applyLevelSuccess<T>(
  state: LevelLoadState<T>,
  generation: number,
  items: T[]
): LevelLoadState<T> {
  if (generation !== state.generation) return state;
  return { ...state, items, failed: false };
}

/**
 * Applies a failed fetch, unless a newer load has started since this one
 * began — in which case the rejection is dropped and the state is returned
 * unchanged. A dropped rejection must not surface an error for a level the
 * user has already moved on from.
 */
export function applyLevelFailure<T>(
  state: LevelLoadState<T>,
  generation: number
): LevelLoadState<T> {
  if (generation !== state.generation) return state;
  return { ...state, items: [], failed: true };
}
